import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users2, Search, Save, Loader2, CheckCircle2, XCircle, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CAN_MAP = ["super_admin", "hr", "manager", "team_lead"];
const APPROVERS = ["super_admin", "manager"];

export default function TeamMapping() {
  const { user } = useAuth();
  const canMap = CAN_MAP.includes(user?.role);
  const isApprover = APPROVERS.includes(user?.role);

  const [tls, setTls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedTL, setSelectedTL] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  // When a TL is picked, default to showing ONLY their mapped coders.
  // Users can flip this to "All eligible" to add or remove members.
  const [mappedOnly, setMappedOnly] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(null);   // request id being rejected
  const [rejectComment, setRejectComment] = useState("");
  const [acting, setActing] = useState(false);

  const loadAll = useCallback(() => {
    api.get("/reporting/team-leads").then((r) => setTls(r.data || []));
    api.get("/employees").then((r) => setEmployees(r.data || []));
    if (canMap) api.get("/reporting/mapping-requests").then((r) => setRequests(r.data || [])).catch(() => {});
  }, [canMap]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // when selectedTL changes, prefill current mappings and revert the view to
  // "Mapped only" so the user sees this TL's existing team by default.
  useEffect(() => {
    if (!selectedTL) { setSelected(new Set()); return; }
    const current = new Set(
      employees.filter((e) => e.team_lead_id === selectedTL).map((e) => e.employee_id)
    );
    setSelected(current);
    setMappedOnly(true);
  }, [selectedTL, employees]);

  const eligible = useMemo(() => {
    // Exclude the TL themselves and Managers/CEOs/Directors
    return employees.filter((e) => {
      if (e.employee_id === selectedTL) return false;
      const d = (e.designation || "").toLowerCase();
      if (/manager|admin manager|ceo|director/.test(d)) return false;
      if (e.status === "exited") return false;
      // When "Mapped only" is on, show solely the coders currently mapped to
      // the selected TL — matches the requested filtering behavior.
      if (mappedOnly && e.team_lead_id !== selectedTL) return false;
      if (query) {
        const q = query.toLowerCase();
        if (
          !(e.name || "").toLowerCase().includes(q) &&
          !(e.employee_id || "").toLowerCase().includes(q) &&
          !(e.designation || "").toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [employees, selectedTL, query, mappedOnly]);

  const toggle = (eid) => {
    const next = new Set(selected);
    if (next.has(eid)) next.delete(eid); else next.add(eid);
    setSelected(next);
  };

  const save = async () => {
    if (!selectedTL) { toast.error("Pick a Team Lead first"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/reporting/bulk-assign-tl", {
        team_lead_emp_id: selectedTL,
        employee_ids: Array.from(selected),
      });
      if (data.applied) {
        toast.success(`Mapped ${data.modified} coders to ${data.team_lead_name}`);
      } else {
        toast.success(`Submitted for Manager approval (${data.employee_ids.length} coder(s))`);
      }
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const approveReq = async (req) => {
    setActing(true);
    try {
      await api.post(`/reporting/mapping-requests/${req.id}/approve`, { comment: "" });
      toast.success(`Approved · ${req.employee_ids.length} coder(s) now under ${req.team_lead_name}`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Approve failed");
    } finally { setActing(false); }
  };

  const submitReject = async () => {
    if (rejectComment.trim().length < 3) { toast.error("Provide a reason"); return; }
    setActing(true);
    try {
      await api.post(`/reporting/mapping-requests/${rejecting}/reject`, { comment: rejectComment });
      toast.success("Request rejected");
      setRejecting(null);
      setRejectComment("");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reject failed");
    } finally { setActing(false); }
  };

  if (!canMap) {
    return <div className="text-sm text-gray-500">You don&apos;t have access to this page.</div>;
  }

  const tlObj = tls.find((t) => t.employee_id === selectedTL);

  return (
    <div data-testid="team-mapping-page">
      <PageHeader
        title="Team Mapping"
        subtitle={
          isApprover
            ? "Map coders directly or review HR / Team Lead submissions for approval."
            : "Submit a mapping of coders under a Team Lead. Manager approval required for it to take effect."
        }
      />

      <Tabs defaultValue="map">
        <TabsList className="bg-transparent border-b border-gray-200 rounded-none w-full justify-start h-auto p-0 gap-6 mb-6">
          <TabsTrigger value="map" data-testid="tab-map" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">
            Create mapping
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">
            {isApprover ? "Pending approvals" : "My requests"}
            {requests.filter((r) => r.status === "pending").length > 0 ? (
              <span className="ml-1.5 text-[10px] bg-[#F26B3A] text-white rounded-full px-1.5">
                {requests.filter((r) => r.status === "pending").length}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="map">

      <Card className="hr-card p-5 mb-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[280px]">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Team Lead</Label>
            <Select value={selectedTL} onValueChange={setSelectedTL}>
              <SelectTrigger data-testid="tm-tl-select" className="mt-1.5">
                <SelectValue placeholder="Pick a Team Lead" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {tls.length === 0 ? (
                  <div className="px-3 py-6 text-xs text-gray-500 text-center">
                    No Team Leads found. Add an employee with designation &quot;Team Lead&quot; or &quot;Process Coach&quot; first.
                  </div>
                ) : tls.map((t) => (
                  <SelectItem key={t.employee_id} value={t.employee_id} data-testid={`tm-tl-${t.employee_id}`}>
                    {t.name} · <span className="text-gray-400 font-mono text-xs">{t.employee_id}</span> · {t.department}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {tlObj && (
            <div className="flex-1 min-w-[220px]">
              <div className="text-xs uppercase tracking-wider text-gray-400">Currently mapped</div>
              <div className="text-sm font-medium text-[#0F172A] mt-1">
                <span className="font-mono">{selected.size}</span> coder(s) under <b>{tlObj.name}</b>
              </div>
            </div>
          )}

          <Button
            data-testid="tm-save"
            onClick={save}
            disabled={!selectedTL || saving}
            className="bg-[#F26B3A] hover:bg-[#d85d2f] h-9"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save mapping
          </Button>
        </div>
      </Card>

      {selectedTL && (
        <Card className="hr-card p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div className="font-display font-semibold text-[#0F172A] flex items-center gap-2">
              <Users2 className="h-4 w-4 text-[#1E88E5]" />
              {mappedOnly ? "Mapped coders" : "Eligible team members"}
              {mappedOnly && tlObj && (
                <span className="text-[11px] font-normal text-gray-500">
                  · {selected.size} under <b>{tlObj.name}</b>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs" data-testid="tm-view-toggle">
                <button
                  type="button"
                  data-testid="tm-view-mapped"
                  onClick={() => setMappedOnly(true)}
                  className={`px-3 py-1.5 ${mappedOnly ? "bg-[#F26B3A] text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                >
                  Mapped only
                </button>
                <button
                  type="button"
                  data-testid="tm-view-all"
                  onClick={() => setMappedOnly(false)}
                  className={`px-3 py-1.5 ${!mappedOnly ? "bg-[#F26B3A] text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                >
                  All eligible
                </button>
              </div>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  data-testid="tm-search"
                  placeholder="Search by name, ID, designation"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9 h-9 w-64"
                />
              </div>
            </div>
          </div>

          <div className="border border-gray-100 rounded-md max-h-[500px] overflow-y-auto">
            {eligible.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-500" data-testid="tm-empty">
                {mappedOnly
                  ? <>No coders are currently mapped to this Team Lead. Switch to <span className="font-medium">All eligible</span> to add some.</>
                  : "No employees match the filter."}
              </div>
            ) : eligible.map((e) => {
              const checked = selected.has(e.employee_id);
              const mappedToOther = e.team_lead_id && e.team_lead_id !== selectedTL;
              return (
                <label key={e.employee_id} className="flex items-center gap-3 px-4 py-2 border-t border-gray-100 hover:bg-gray-50 cursor-pointer first:border-t-0" data-testid={`tm-row-${e.employee_id}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(e.employee_id)}
                    className="h-4 w-4"
                    data-testid={`tm-cb-${e.employee_id}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#0F172A]">{e.name}</div>
                    <div className="text-[11px] text-gray-500">
                      <span className="font-mono">{e.employee_id}</span> · {e.designation || "—"} · {e.department || "—"}
                    </div>
                  </div>
                  {mappedToOther && !checked && (
                    <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      under {e.team_lead_name}
                    </span>
                  )}
                  {checked && (
                    <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                      in this team
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </Card>
        )}
        </TabsContent>

        <TabsContent value="requests">
          <div className="space-y-3" data-testid="mapping-requests-list">
            {requests.length === 0 ? (
              <Card className="hr-card p-10 text-center text-sm text-gray-500">
                <Inbox className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                {isApprover ? "No mapping requests awaiting approval." : "You haven't submitted any mapping requests yet."}
              </Card>
            ) : requests.map((req) => {
              const status = req.status || "pending";
              const colors = {
                pending:  "bg-amber-100 text-amber-800",
                approved: "bg-emerald-100 text-emerald-800",
                rejected: "bg-rose-100 text-rose-800",
              };
              return (
                <Card key={req.id} className="hr-card p-4" data-testid={`mapping-req-${req.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-sm font-medium text-[#0F172A]">
                        {req.employee_ids?.length || 0} coder(s) → <b>{req.team_lead_name}</b>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        Requested by {req.requested_by} ({req.requested_by_role}) · {(req.requested_at || "").slice(0, 10)}
                      </div>
                      {req.rejection_reason && (
                        <div className="text-[11px] text-rose-700 mt-1">Rejected: {req.rejection_reason}</div>
                      )}
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${colors[status] || "bg-gray-100 text-gray-700"}`}>
                      {status}
                    </span>
                    {isApprover && status === "pending" && (
                      <div className="flex gap-1.5">
                        <Button data-testid={`mapping-approve-${req.id}`} size="sm" variant="outline" className="h-8" onClick={() => approveReq(req)} disabled={acting}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Approve
                        </Button>
                        <Button data-testid={`mapping-reject-${req.id}`} size="sm" variant="outline" className="h-8" onClick={() => setRejecting(req.id)} disabled={acting}>
                          <XCircle className="h-3.5 w-3.5 mr-1 text-rose-600" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                  {rejecting === req.id && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <Label className="text-xs uppercase tracking-wider text-gray-500">Reason for rejection</Label>
                      <Input
                        data-testid={`mapping-reject-reason-${req.id}`}
                        className="mt-1.5"
                        value={rejectComment}
                        onChange={(e) => setRejectComment(e.target.value)}
                        placeholder="e.g. Coder XYZ should remain under their original TL"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button variant="outline" size="sm" onClick={() => { setRejecting(null); setRejectComment(""); }}>Cancel</Button>
                        <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" data-testid={`mapping-reject-confirm-${req.id}`} onClick={submitReject} disabled={acting}>
                          {acting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm reject"}
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
